import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import { type InventoryStore } from '@/features/inventory/InventoryStore'
import { type RefreshInventoryItems } from '@/features/inventory/admin/InventorySearchViewModel'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'

/**
 * Confirms and archives a service item.
 */
export class ArchiveItemDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor inventoryItem: InventoryItem | null = null

  constructor(
    private readonly _inventoryStore: InventoryStore,
    private readonly _refreshItems: RefreshInventoryItems,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Asks to archive the item.
   *
   * @param item
   * @param signal
   * @returns `true` if the item was archived, `false` if the user cancelled.
   */
  async prompt(item: InventoryItem, signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.inventoryItem = item
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  archive = task.resolved(async () => {
    if (!this.inventoryItem) {
      return
    }

    const item = this.inventoryItem
    await this._inventoryStore
      .archiveInventoryItem(item.id)
      .catch(this._createAlertErrorHandler('Archiving the item failed'))

    this._newAlert()
      .withState('success')
      .saying('Success', 'Item archived.')
      .autoDismiss()
      .show()

    await this._refreshItems(item.workspace ?? Workspace.none)

    this._prompt.respond(true)
    this.modal.close()
  })
}
